import { create } from "zustand";
// import axiosInstance from "../config/axiosConfig"
import {
  addFeedbackToMessage,
  chatHistoryNavigation,
  fetchChatHistoryBySessionIndex,
} from "../services/chatbotService";

const DEFAULT_AI_NAME = "Elise AI";

// Helper function to safely parse and format timestamps
const safeFormatTimestamp = (timestamp: string | null | undefined): string => {
  if (!timestamp) return "";
  try {
    const date = new Date(timestamp);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleString();
  } catch (error) {
    // Silently handle errors - invalid timestamps return empty string
    return "";
  }
};

export interface UploadedFileFromAPI {
  file_url: string;
  file_name: string;
  collection_name: string;
}

export interface Message {
  id: number;
  project_id: string;
  _id: string;
  index_id: number;
  sender: "user" | "ai";
  name: string;
  avatar: string;
  time: string;
  content: React.ReactNode;
  isSpeaking?: boolean;
  isTemp?: boolean;
  user_id?: string;
  project_name?: string;
  chat_session_id?: string;
  message?: string;
  ai_response?: string | null;
  created_at?: string;
  updated_at?: string;
  thumbs_up?: boolean;
  thumbs_down?: boolean;
  reaction_description?: string; // Store the comment for undo functionality
  reply_message_id?: string | null; // Optional field for reply message ID
  reply_message?: string | null; // Optional field for reply message
  type?: string | null; // Optional field for message type
  uploaded_files?: UploadedFileFromAPI[]; // Optional field for uploaded files from API
}

interface ChatSessionStore {
  chatMessages: Message[];
  chatMessageIsLoading: boolean;
  setChatMessageIsLoading: (loading: boolean) => void;
  chatMessageAppendIsLoading: boolean;
  setChatMessageAppendIsLoading: (loading: boolean) => void;
  setChatMessageError: (error: string | null) => void;
  chatMessageError: string | null;
  setChatMessages: (sessions: Message[]) => void;
  appendAtEndChatMessages: (
    index_id?: number,
    action?: string
  ) => Promise<void>;
  appendAtBottomChatMessages: (index_id?: number) => Promise<void>;
  chatMessagePrependIsLoading: boolean;
  setChatMessagePrependIsLoading: (loading: boolean) => void;
  replaceChatMessages: (
    project_id: string,
    index_id: number,
    action?: string,
    session_index?: number
  ) => Promise<void>;
  clearChatMessages: () => void;
  clearAndSetChatMessages: () => Promise<void>;
  refreshChatMessages: () => Promise<void>; // New function to replace all messages
  submitFeedback: ({
    chatMessageId,
    comment,
    voteType,
  }: {
    chatMessageId: string;
    comment: string;
    voteType: "like" | "dislike";
  }) => Promise<void>;
  topCurrentIndexId?: number; // Optional, used for tracking current index
  setTopCurrentIndexId?: (indexId: number) => void; // Optional,
  bottomCurrentIndexId?: number; // Optional, used for tracking current index
  setBottomCurrentIndexId?: (indexId: number) => void; // Optional
  showSuggestions: boolean; // Optional, used for showing suggestions
  setShowSuggestions: (show: boolean) => void; // Optional setter for showSuggestions
  replyMessage: Message | null; // Optional field for reply message
  replyToMessageId: string | null; // Optional field for reply message ID
  replyToIndexId: number | null; // Optional field for reply message index ID
  setReplyMessage: (message: Message | null) => void; // Optional setter for reply message
  setReplyToMessageId: (id: string | null) => void; // Optional
  setReplyToIndexId: (indexId: number | null) => void; // Optional setter for reply message index ID
  IsWebEnabled: boolean; // Optional field to indicate if the app is web-only
  setIsWebEnabled: (isWebEnabled: boolean) => void; // Optional setter

  // NEW: AI Response Loading State
  isAIResponding: boolean;
  setIsAIResponding: (isResponding: boolean) => void;
  aiAssistantName: string;
  setAiAssistantName: (name?: string | null) => void;
}

const useChatMessageStore = create<ChatSessionStore>((set, get) => ({
  chatMessages: [],
  chatMessageIsLoading: false,
  chatMessageError: null,
  showSuggestions: false, // Optional field for showing suggestions
  chatMessagePrependIsLoading: false,
  IsWebEnabled: false, // Optional field to indicate if the app is web-only

  // NEW: AI Response Loading State
  isAIResponding: false,
  setIsAIResponding: (isResponding: boolean) =>
    set({ isAIResponding: isResponding }),
  aiAssistantName: DEFAULT_AI_NAME,
  setAiAssistantName: (name) =>
    set({ aiAssistantName: name?.trim() ? name.trim() : DEFAULT_AI_NAME }),

  setIsWebEnabled: (isWebEnabled) => set({ IsWebEnabled: isWebEnabled }),
  setShowSuggestions: (show) => set({ showSuggestions: show }),
  setChatMessagePrependIsLoading: (loading) =>
    set({ chatMessagePrependIsLoading: loading }),
  setChatMessages: (sessions) =>
    set({
      chatMessages: sessions,
    }),
  setChatMessageIsLoading: (loading) => set({ chatMessageIsLoading: loading }),
  chatMessageAppendIsLoading: false,
  setChatMessageAppendIsLoading: (loading) =>
    set({ chatMessageAppendIsLoading: loading }),
  setChatMessageError: (error) => set({ chatMessageError: error }),
  clearAndSetChatMessages: async () => {
    set({
      chatMessageIsLoading: true,
      chatMessageError: null,
    });
    try {
      const response = await fetchChatHistoryBySessionIndex();
      if (response.session_chats && response.session_chats.length > 0) {
        const aiAssistantName = get().aiAssistantName || DEFAULT_AI_NAME;
        set((state) => {
          // Map new response data structure to Message[]
          const newMessages = response.session_chats.flatMap(
            (item, idx) => {
              const userMsg: Message = {
                id: idx * 2 + 1,
                project_id: "", // Not available in new structure
                _id: item.chat_id.toString(),
                index_id: item.session_id,
                sender: "user",
                name: "User",
                avatar: "",
                time: safeFormatTimestamp(item.created_at),
                content: String(item.message || ""),
                isSpeaking: false,
                isTemp: false,
                project_name: item.agent_type,
                user_id: "",
                chat_session_id: item.session_id.toString(),
                message: item.message,
                ai_response: item.response,
                created_at: item.created_at,
                updated_at: item.created_at,
                thumbs_down: item.reaction_type === "dislike",
                thumbs_up: item.reaction_type === "like",
                reply_message_id: item.replied_to?.toString() || null,
                reply_message: item.replied_message,
                type: item.chat_type,                
              };
              const aiMsg = item.response
                ? {
                    ...userMsg,
                    id: idx * 2 + 2,
                    sender: "ai" as const,
                    name: aiAssistantName,
                    avatar: "", // set your aiAvatar here if needed
                    time: safeFormatTimestamp(item.created_at),
                    content: String(item.response || ""),
                  }
                : null;
              return aiMsg ? [userMsg, aiMsg] : [userMsg];
            }
          );

          // Avoid duplicates by _id
          const existingIds = new Set(state.chatMessages.map((m) => m._id));
          const filteredNewMessages = newMessages.filter(
            (m) => !existingIds.has(m._id)
          );

          return {
            chatMessages: [...filteredNewMessages],
            chatMessageIsLoading: false,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              filteredNewMessages.length > 0
                ? filteredNewMessages[0]?.index_id
                : undefined,
            bottomCurrentIndexId:
              filteredNewMessages.length > 0
                ? filteredNewMessages[filteredNewMessages.length - 1]?.index_id
                : undefined,
          };
        });
      }
    } catch (error: any) {
      set({
        chatMessageIsLoading: false,
        chatMessageError: error?.message || "Failed to fetch messages",
      });
    }
  },
  appendAtEndChatMessages: async (index_id, action) => {
    set({
      chatMessageAppendIsLoading: true,
      chatMessageError: null,
      showSuggestions: false,
    });
    try {
      let response;
      if (action) {
        response = await fetchChatHistoryBySessionIndex(
          index_id,
          action as "up" | "down"
        );
      } else {
        response = await fetchChatHistoryBySessionIndex(index_id);
      }
      if (response.session_chats && response.session_chats.length > 0) {
        const aiAssistantName = get().aiAssistantName || DEFAULT_AI_NAME;
        set((state) => {
          // Map new response data structure to Message[]
          const newMessages = response.session_chats.flatMap(
            (item, idx) => {
              const userMsg: Message = {
                id: idx * 2 + 1,
                project_id: "", // Not available in new structure
                _id: item.chat_id.toString(),
                index_id: item.session_id,
                sender: "user",
                name: "User",
                avatar: "",
                time: safeFormatTimestamp(item.created_at),
                content: String(item.message || ""),
                isSpeaking: false,
                isTemp: false,
                project_name: item.agent_type,
                user_id: "",
                chat_session_id: item.session_id.toString(),
                message: item.message,
                ai_response: item.response,
                created_at: item.created_at,
                updated_at: item.created_at,
                thumbs_down: item.reaction_type === "dislike",
                thumbs_up: item.reaction_type === "like",
                reply_message_id: item.replied_to?.toString() || null,
                reply_message: item.replied_message,
                type: item.chat_type,
                uploaded_files: (item as any).uploaded_files || undefined,
              };
              const aiMsg = item.response
                ? {
                    ...userMsg,
                    id: idx * 2 + 2,
                    sender: "ai" as const,
                    name: aiAssistantName,
                    avatar: "", // set your aiAvatar here if needed
                    time: safeFormatTimestamp(item.created_at),
                    content: String(item.response || ""),
                  }
                : null;
              return aiMsg ? [userMsg, aiMsg] : [userMsg];
            }
          );

          // Avoid duplicates by _id
          const existingIds = new Set(state.chatMessages.map((m) => m._id));
          const filteredNewMessages = newMessages.filter(
            (m) => !existingIds.has(m._id)
          );

          const allMessages = [...filteredNewMessages, ...state.chatMessages];

          return {
            chatMessages: allMessages,
            chatMessageAppendIsLoading: false,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              allMessages.length > 0
                ? allMessages[0]?.index_id
                : undefined,
            bottomCurrentIndexId:
              allMessages.length > 0
                ? allMessages[allMessages.length - 1]?.index_id
                : undefined,
          };
        });
      } else {
        set((state) => {
          return {
            chatMessageAppendIsLoading: false,
            showSuggestions: state.chatMessages.length === 0, // Show suggestions if no messages
            // Clear topCurrentIndexId when no more messages are available (reached the beginning)
            topCurrentIndexId: undefined,
          };
        });
      }
    } catch (error: any) {
      set({
        chatMessageAppendIsLoading: false,
        chatMessageError: error?.message || "Failed to fetch messages",
      });
    }
  },
  appendAtBottomChatMessages: async (index_id) => {
    set({
      chatMessagePrependIsLoading: true,
      chatMessageError: null,
    });
    try {
      const response = await fetchChatHistoryBySessionIndex(index_id, "down");
      if (response.session_chats && response.session_chats.length > 0) {
        const aiAssistantName = get().aiAssistantName || DEFAULT_AI_NAME;
        set((state) => {
          // Map new response data structure to Message[]
          const newMessages = response.session_chats.flatMap(
            (item, idx) => {
              const userMsg: Message = {
                id: idx * 2 + 1,
                project_id: "", // Not available in new structure
                _id: item.chat_id.toString(),
                index_id: item.session_id,
                sender: "user",
                name: "User",
                avatar: "",
                time: safeFormatTimestamp(item.created_at),
                content: String(item.message || ""),
                isSpeaking: false,
                isTemp: false,
                project_name: item.agent_type,
                user_id: "",
                chat_session_id: item.session_id.toString(),
                message: item.message,
                ai_response: item.response,
                created_at: item.created_at,
                updated_at: item.created_at,
                thumbs_down: item.reaction_type === "dislike",
                thumbs_up: item.reaction_type === "like",
                reply_message_id: item.replied_to?.toString() || null,
                reply_message: item.replied_message,
                type: item.chat_type,
                uploaded_files: (item as any).uploaded_files || undefined,
              };
              const aiMsg = item.response
                ? {
                    ...userMsg,
                    id: idx * 2 + 2,
                    sender: "ai" as const,
                    name: aiAssistantName,
                    avatar: "", // set your aiAvatar here if needed
                    time: safeFormatTimestamp(item.created_at),
                    content: String(item.response || ""),
                  }
                : null;
              return aiMsg ? [userMsg, aiMsg] : [userMsg];
            }
          );

          // Avoid duplicates by _id
          const existingIds = new Set(state.chatMessages.map((m) => m._id));
          const filteredNewMessages = newMessages.filter(
            (m) => !existingIds.has(m._id)
          );
          const allMessages = [...state.chatMessages, ...filteredNewMessages];
          return {
            chatMessages: allMessages,
            chatMessagePrependIsLoading: false,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              allMessages.length > 0 ? allMessages[0]?.index_id : undefined,
            bottomCurrentIndexId:
              allMessages.length > 0
                ? allMessages[allMessages.length - 1]?.index_id
                : undefined,
          };
        });
      } else {
        set({
          chatMessagePrependIsLoading: false,
          chatMessageError: "No new messages to prepend",
        });
      }
    } catch (error: any) {
      set({
        chatMessagePrependIsLoading: false,
        chatMessageError: error?.message || "Failed to fetch messages",
      });
    }
  },
  replaceChatMessages: async (project_id, index_id, action, session_index) => {
    set({
      chatMessageIsLoading: true,
      chatMessageError: null,
    });
    try {
      const response = await chatHistoryNavigation({
        entity: project_id,
        session_id: index_id,
        chat_order_action: action || undefined,
        session_index: session_index,
      });
      if (response.session_chats && response.session_chats.length > 0) {
        const aiAssistantName = get().aiAssistantName || DEFAULT_AI_NAME;
        set((state) => {
          // If response.session_chats is not an array, return current state
          if (!Array.isArray(response.session_chats)) {
            return state; // No messages to replace
          }
          // Map response data to Message[]
          const newMessages = response.session_chats.flatMap(
            (item, idx) => {
              const userMsg: Message = {
                id: idx * 2 + 1,
                project_id: "", // Not available in navigation response
                _id: item.chat_id.toString(),
                index_id: item.session_id,
                sender: "user",
                name: "User",
                avatar: "",
                time: safeFormatTimestamp(item.created_at),
                content: String(item.message || ""),
                isSpeaking: false,
                isTemp: false,
                project_name: item.agent_type,
                user_id: "",
                chat_session_id: item.session_id.toString(),
                message: item.message,
                ai_response: item.response,
                created_at: item.created_at,
                updated_at: item.created_at,
                thumbs_down: item.reaction_type === "dislike",
                thumbs_up: item.reaction_type === "like",
                reply_message_id: item.replied_to?.toString() || null,
                reply_message: item.replied_message,
                type: item.chat_type,
                uploaded_files: (item as any).uploaded_files || undefined,
              };
              const aiMsg = item.response
                ? {
                    ...userMsg,
                    id: idx * 2 + 2,
                    sender: "ai" as const,
                    name: aiAssistantName,
                    project_name: item.agent_type,
                    avatar: "",
                    time: safeFormatTimestamp(item.created_at),
                    content: String(item.response || ""),
                   
                  }
                : null;
              return aiMsg ? [userMsg, aiMsg] : [userMsg];
            }
          );
          return {
            chatMessages: newMessages,
            chatMessageIsLoading: false,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              newMessages.length > 0 ? newMessages[0]?.index_id : undefined,
            bottomCurrentIndexId:
              newMessages.length > 0
                ? newMessages[newMessages.length - 1]?.index_id
                : undefined,
          };
        });
      }
    } catch (error: any) {
      set({
        chatMessageIsLoading: false,
        chatMessageError: error?.message || "Failed to fetch messages",
      });
    }
  },
  clearChatMessages: () =>
    set({
      chatMessages: [],
      topCurrentIndexId: undefined,
      bottomCurrentIndexId: undefined,
      chatMessageIsLoading: false,
      chatMessageAppendIsLoading: false,
      chatMessageError: null,
      chatMessagePrependIsLoading: false,
      showSuggestions: false, // Reset showSuggestions when clearing messages
      isAIResponding: false, // Reset AI responding state when clearing
    }),
  refreshChatMessages: async () => {
    set({
      chatMessageIsLoading: true,
      chatMessageError: null,
    });
    try {
      const response = await fetchChatHistoryBySessionIndex();
      if (response.session_chats && response.session_chats.length > 0) {
        const aiAssistantName = get().aiAssistantName || DEFAULT_AI_NAME;
        set((_state) => {
          // console.log(_state)
          // Map new response data structure to Message[]
          const newMessages = response.session_chats.flatMap(
            (item, idx) => {
              const userMsg: Message = {
                id: idx * 2 + 1,
                project_id: "", // Not available in new structure
                _id: item.chat_id.toString(),
                index_id: item.session_id,
                sender: "user",
                name: "User",
                avatar: "",
                time: safeFormatTimestamp(item.created_at),
                content: String(item.message || ""),
                isSpeaking: false,
                isTemp: false,
                project_name: item.agent_type,
                user_id: "",
                chat_session_id: item.session_id.toString(),
                message: item.message,
                ai_response: item.response,
                created_at: item.created_at,
                updated_at: item.created_at,
                thumbs_down: item.reaction_type === "dislike",
                thumbs_up: item.reaction_type === "like",
                reply_message_id: item.replied_to?.toString() || null,
                reply_message: item.replied_message,
                type: item.chat_type,
                uploaded_files: (item as any).uploaded_files || undefined,
              };
              const aiMsg = item.response
                ? {
                    ...userMsg,
                    id: idx * 2 + 2,
                    sender: "ai" as const,
                    name: aiAssistantName,
                    avatar: "", // set your aiAvatar here if needed
                    time: safeFormatTimestamp(item.created_at),
                    content: String(item.response || ""),
                    project_name: item.agent_type,
                  }
                : null;
              return aiMsg ? [userMsg, aiMsg] : [userMsg];
            }
          );

          // Replace all messages with fresh data from API (no filtering)
          return {
            chatMessages: newMessages,
            chatMessageIsLoading: false,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              newMessages.length > 0 ? newMessages[0]?.index_id : undefined,
            bottomCurrentIndexId:
              newMessages.length > 0
                ? newMessages[newMessages.length - 1]?.index_id
                : undefined,
          };
        });
      }
    } catch (error: any) {
      set({
        chatMessageIsLoading: false,
        chatMessageError: error?.message || "Failed to fetch messages",
      });
    }
  },
  submitFeedback: async ({ chatMessageId, comment, voteType }) => {
    try {
      // Check if we're undoing (message already has this feedback)
      const currentMessage = get().chatMessages.find(
        (msg) => msg._id == chatMessageId
      );
      const isUndo =
        (voteType === "like" && currentMessage?.thumbs_up) ||
        (voteType === "dislike" && currentMessage?.thumbs_down);

      const response = await addFeedbackToMessage({
        chat_id: parseInt(chatMessageId),
        reaction_type: voteType,
        reaction_description: comment || undefined,
      });
      if (response) {
        set((state) => {
          const updatedMessages = state.chatMessages.map((msg) => {
            if (msg._id == chatMessageId) {
              // If undoing, set both to false and clear comment; otherwise set based on voteType
              if (isUndo) {
                return {
                  ...msg,
                  thumbs_up: false,
                  thumbs_down: false,
                  reaction_description: undefined,
                };
              } else {
                return {
                  ...msg,
                  thumbs_up: voteType === "like",
                  thumbs_down: voteType === "dislike",
                  reaction_description: comment || undefined,
                };
              }
            }
            return msg;
          });
          return {
            chatMessages: updatedMessages,
            //chat messages last element ID will be used to bottomCurrentIndexId and first element ID will be used to topCurrentIndexId
            topCurrentIndexId:
              updatedMessages.length > 0
                ? updatedMessages[0]?.index_id
                : undefined,
            bottomCurrentIndexId:
              updatedMessages.length > 0
                ? updatedMessages[updatedMessages.length - 1]?.index_id
                : undefined,
          };
        });
      } else {
        console.error("Failed to submit feedback");
      }
    } catch (error: any) {
      console.error("Error submitting feedback:", error?.message || error);
    }
  },
  replyMessage: null, // Optional field for reply message
  replyToMessageId: null, // Optional field for reply message ID
  replyToIndexId: null, // Optional field for reply message index ID
  setReplyMessage: (message) => set({ replyMessage: message }), // Optional setter
  setReplyToMessageId: (id) => set({ replyToMessageId: id }), // Optional setter
  setReplyToIndexId: (indexId) => set({ replyToIndexId: indexId }), // Optional setter
}));

export default useChatMessageStore;
